import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  Select,
  LinearProgress,
  MenuItem,
  Divider,
  Paper,
  Skeleton,
} from "@mui/material";
import {
  RemoveRedEyeOutlined,
  PersonOutlineOutlined,
  CheckCircleOutline,
  TrendingUpOutlined,
  TrendingDownOutlined,
  AutorenewRounded,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  Legend,
} from "recharts";
import CommonHeader from "../../CommonComponent/CommonHeader";
import {
  getDashboardConsents,
  getDashboardCountires,
  getDashboardDevices,
  getDashboardVisitors,
  getDashboardPageVisitStats,
  getDashboardConsentStats,
  getDashboardRequestStats,
} from "../../api/dashboard";
import Loader from "../../CommonComponent/Loader";
import OnboardingProgress from "./OnboardingProgress";
import { fetchUserAttributes } from "@aws-amplify/auth";
import Flag from "react-world-flags";
import { getWebsiteById } from "../../api/websiteService";
import { convertHourToTimezone } from "../../utils/dateUtils";
import { getConsentDetails } from "../../api/consentLog";
import { ClosedDataRequests, OpenDataRequests, TotalDataRequests } from "../../assets/svg";
import formatNumber from "../../utils/formatNumber";

const Dashboard = () => {
  const [countries, setCountries] = useState([]);
  const [consents, setConsents] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [device, setDevice] = useState([]);
  const [selectedDays, setSelectedDays] = useState(-24); // Default to last 24 hours (hourly)

  // Split stats state (replaces old combined stats)
  const [pageVisitStats, setPageVisitStats] = useState({});
  const [consentStatsData, setConsentStatsData] = useState({});
  const [requestStats, setRequestStats] = useState({});

  // Removed global loading state to allow individual skeleton loading
  const [loadingWebsiteId, setLoadingWebsiteId] = useState(true);
  const [loadingWebsiteDetails, setLoadingWebsiteDetails] = useState(true);
  const [loadingConsent, setLoadingConsent] = useState(true);
  
  // Individual loading states for each panel (using split stats APIs)
  const [loadingStates, setLoadingStates] = useState({
    pageVisitStats: true,  // Page views and unique visitors
    consentStats: true,    // Consent count
    requestStats: true,    // Total, open, closed requests
    countries: true,
    visitors: true,
    consents: true,
    devices: true
  });
  const [isOnboardingRequired, setIsOnboardingRequired] = useState(false);
  const [websiteId, setWebsiteId] = useState(null);
  const [timezone, setTimezone] = useState(null);

  // Fetch website ID and store it
  const fetchWebsiteById = async () => {
    try {
      setLoadingWebsiteId(true);
      const user = await fetchUserAttributes();
      const id = user?.website;

      if (id) {
        localStorage.setItem("website_id", id);
        setWebsiteId(id); // Set websiteId state
      }
    } catch (error) {
    } finally {
      setLoadingWebsiteId(false);
    }
  };

  // Fetch website details like timezone
  const getWebsiteDetails = async (id) => {
    setLoadingWebsiteDetails(true);
    try {
      const websiteData = await getWebsiteById(id);
      const timezoneFromApi = websiteData?.data?.time_zone;
      setTimezone(timezoneFromApi);
      setLoadingWebsiteDetails(false);
    } catch (err) {
      setLoadingWebsiteDetails(false);
    }
  };

  // Fetch all dashboard data with individual loading states
  const fetchAllData = async (id) => {
    // Set all panels to loading
    setLoadingStates({
      pageVisitStats: true,
      consentStats: true,
      requestStats: true,
      countries: true,
      visitors: true,
      consents: true,
      devices: true
    });

    try {
      // New split stats API calls for better performance - replaces old getDashboardStats()
      const fetchPageVisitStats = getDashboardPageVisitStats(selectedDays).then(response => {
        setPageVisitStats(response?.data || {});
        setLoadingStates(prev => ({ ...prev, pageVisitStats: false }));
        return response;
      });

      const fetchConsentStats = getDashboardConsentStats(selectedDays).then(response => {
        setConsentStatsData(response?.data || {});
        setLoadingStates(prev => ({ ...prev, consentStats: false }));
        return response;
      });

      const fetchRequestStats = getDashboardRequestStats(selectedDays).then(response => {
        setRequestStats(response?.data || {});
        setLoadingStates(prev => ({ ...prev, requestStats: false }));
        return response;
      });

      const fetchCountries = getDashboardCountires(selectedDays).then(response => {
        setCountries(response?.data || []);
        setLoadingStates(prev => ({ ...prev, countries: false }));
        return response;
      });

      const fetchDevices = getDashboardDevices(selectedDays).then(response => {
        const devicesData = Array.isArray(response?.data) ? response.data : [];
        setDevice(devicesData);
        setLoadingStates(prev => ({ ...prev, devices: false }));
        return response;
      });

      const fetchConsents = getDashboardConsents(selectedDays).then(response => {
        const consentsData = Array.isArray(response?.data)
          ? response.data.map((item) => ({
            date:
              selectedDays === -24
                ? convertHourToTimezone(item.date, timezone)
                : item.date,
            fullConsents: parseInt(item.full_consent),
            customConsents: parseInt(item.custom_consent),
            onlyNecessary: parseInt(item.only_necessary),
            noDecision: parseInt(item.no_decision),
          }))
          : [];
        setConsents(consentsData);
        setLoadingStates(prev => ({ ...prev, consents: false }));
        return response;
      });

      const fetchVisitors = getDashboardVisitors(selectedDays).then(response => {
        const visitorsData = Array.isArray(response?.data)
          ? response.data.map((item) => ({
            date:
              selectedDays === -24
                ? convertHourToTimezone(item.date, timezone)
                : item.date,
            pageViews: parseInt(item.pageviews),
            uniqueVisitors: parseInt(item.unique_visitors),
          }))
          : [];
        setVisitors(visitorsData);
        setLoadingStates(prev => ({ ...prev, visitors: false }));
        return response;
      });

      // Execute all requests independently - no waiting for all to complete
      // Each API call will update its own loading state when it completes
      fetchPageVisitStats.catch(error => {
        console.error('Page Visit Stats API failed:', error);
        setLoadingStates(prev => ({ ...prev, pageVisitStats: false }));
      });
      
      fetchConsentStats.catch(error => {
        console.error('Consent Stats API failed:', error);
        setLoadingStates(prev => ({ ...prev, consentStats: false }));
      });
      
      fetchRequestStats.catch(error => {
        console.error('Request Stats API failed:', error);
        setLoadingStates(prev => ({ ...prev, requestStats: false }));
      });
      
      fetchCountries.catch(error => {
        console.error('Countries API failed:', error);
        setLoadingStates(prev => ({ ...prev, countries: false }));
      });
      
      fetchDevices.catch(error => {
        console.error('Devices API failed:', error);
        setLoadingStates(prev => ({ ...prev, devices: false }));
      });
      
      fetchConsents.catch(error => {
        console.error('Consents API failed:', error);
        setLoadingStates(prev => ({ ...prev, consents: false }));
      });
      
      fetchVisitors.catch(error => {
        console.error('Visitors API failed:', error);
        setLoadingStates(prev => ({ ...prev, visitors: false }));
      });

    } catch (error) {
      // Global error handler - this should rarely be reached since individual errors are handled above
      console.error('Unexpected error in fetchAllData:', error);
      // Don't reset all loading states here since individual API calls handle their own errors
    }
  };

  const getConsentLogs = async () => {
    setLoadingConsent(true);
    try {
      const res = await getConsentDetails();
      // Check if API returned 404 or empty data
      if (
        res?.status_code === 404 ||
        !res?.data ||
        Object.keys(res.data).length === 0
      ) {
        setIsOnboardingRequired(true);
        return;
      }
    } catch (err) {
    } finally {
      setLoadingConsent(false);
    }
  };

  useEffect(() => {
    const initializeWebsite = async () => {
      try {
        await fetchWebsiteById();
        getConsentLogs();
      } catch (error) { 
        console.log(error);
      }
    };
    initializeWebsite();
  }, []);

  // After websiteId is set, fetch website details
  useEffect(() => {
    if (websiteId) {
      getWebsiteDetails(websiteId);
    }
  }, [websiteId]);

  useEffect(() => {
    if (websiteId && timezone !== null) {
      fetchAllData(websiteId);
    }
    // eslint-disable-next-line
  }, [websiteId, timezone, selectedDays]);

  const refreshData = () => {
    fetchAllData();
  };

  // const formatNumber = (num) => {
  //   if (num === null || num === undefined) return "0";
  //   const number = Number(num);
  //   if (number >= 1_000_000_000) {
  //     return (number / 1_000_000_000).toFixed(1) + "B";
  //   } else if (number >= 1_000_000) {
  //     return (number / 1_000_000).toFixed(1) + "M";
  //   } else if (number >= 1_000) {
  //     return (number / 1_000).toFixed(1) + "K";
  //   } else {
  //     return number.toLocaleString();
  //   }
  // };

  const StatCard = ({ icon, title, value, growth, isLoading = false, gradientColors = ['#00B894', '#00E7AC'] }) => (
    <Card
      variant="outlined"
      sx={{
        p: 3,
        height: "100%",
        borderRadius: 2,
        bgcolor: "white",
        pb: 0,
        pl: 0,
        pr: 0,
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid #E5E7EB',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        '&:hover': {
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          transform: 'translateY(-2px)',
          transition: 'all 0.2s ease-in-out'
        }
      }}
    >
      {/* Loading Progress Bar */}
      {isLoading && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: 'linear-gradient(90deg, rgba(0, 184, 148, 0.1) 0%, rgba(0, 231, 172, 0.1) 100%)',
            '& .MuiLinearProgress-bar': {
              background: `linear-gradient(90deg, ${gradientColors[0]} 0%, ${gradientColors[1]} 100%)`,
            },
          }}
        />
      )}
      
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          pl: 3,
          opacity: isLoading ? 0.6 : 1,
          transition: 'opacity 0.3s ease-in-out'
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "row" }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(135deg, ${gradientColors[0]}15 0%, ${gradientColors[1]}25 100%)`,
              mr: 2,
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: "400", color: "#6B7280" }}
            >
              {title}
            </Typography>
            {isLoading ? (
              <Skeleton 
                variant="text" 
                width={80} 
                height={32}
                animation="wave"
                sx={{
                  borderRadius: 1,
                  bgcolor: `${gradientColors[0]}20`
                }}
              />
            ) : (
              <Typography
                variant="h6"
                sx={{ 
                  mb: 1, 
                  fontWeight: "bold", 
                  color: "#111827",
                  background: `linear-gradient(90deg, ${gradientColors[0]} 0%, ${gradientColors[1]} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                {formatNumber(value, timezone)}
              </Typography>
            )}
          </Box>
        </Box>
        {!isLoading && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              background:
                growth > 0 ? "linear-gradient(135deg, #ECFDF5 0%, #F0FDF4 100%)" : 
                growth < 0 ? "linear-gradient(135deg, #FEF2F2 0%, #FEFCFC 100%)" : 
                "linear-gradient(135deg, #F3F4F6 0%, #F9FAFB 100%)",
              padding: "6px 12px",
              borderRadius: 12,
              mr: 2,
              border: growth > 0 ? "1px solid #D1FAE5" : growth < 0 ? "1px solid #FECACA" : "1px solid #E5E7EB"
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color:
                  growth > 0 ? "#059669" : growth < 0 ? "#DC2626" : "#6B7280",
                fontWeight: "600",
                fontSize: "0.75rem"
              }}
            >
              {growth > 0 ? (
                <TrendingUpOutlined sx={{ fontSize: 14, mr: 0.5 }} />
              ) : growth < 0 ? (
                <TrendingDownOutlined sx={{ fontSize: 14, mr: 0.5 }} />
              ) : (
                <TrendingUpOutlined sx={{ fontSize: 14, mr: 0.5 }} />
              )}
              {growth !== 0 && (growth > 0 ? "+" : "")}
              {Math.round(growth)}%
            </Typography>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          background: "linear-gradient(90deg, #F8FAFC 0%, #F1F5F9 100%)",
          pl: 3,
          py: 1.5,
          borderTop: "1px solid #F1F5F9"
        }}
      >
        <Typography
          variant="body2"
          sx={{ 
            fontSize: "0.75rem",
            fontWeight: 500,
            color: "#64748B"
          }}
        >
          {selectedDays === -24 ? "last 24 hours" : selectedDays < 0 ? `last ${Math.abs(selectedDays)} hours` : `last ${selectedDays} days`}
        </Typography>
      </Box>
    </Card>
  );

  // if (loading || loadingWebsiteId || loadingWebsiteDetails) {
  //   <Loader />;
  // }

  const getLast30DaysRange = () => {
    const currentDate = new Date();
    const pastDate = new Date(currentDate);
    pastDate.setDate(currentDate.getDate() - selectedDays);

    const startDate = pastDate?.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });
    const endDate = currentDate?.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });

    return `${startDate} - ${endDate}`;
  };

  const handleTimePeriodChange = (event) => {
    const newPeriod = event.target.value;
    setSelectedDays(newPeriod); // Update the selected days
  };

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) {
      return null;
    }

    const data = payload[0]?.payload;

    return (
      <Paper
        elevation={3}
        sx={{
          p: 2,
          minWidth: 200,
          backgroundColor: "white",
          borderRadius: 1,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
            {data?.name}
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Total
            </Typography>
            <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
              <Typography
                variant="body"
                color="#525455"
                sx={{ fontWeight: 400 }}
              >
                {formatNumber(data?.value, timezone)}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  background:
                    data?.percentage > 0
                      ? "#F0FFFC"
                      : data?.percentage < 0
                        ? "#FFEFF2"
                        : "#E0E0E0", // Grey background for growth = 0
                  padding: "4px 8px",
                  borderRadius: 2,
                  mr: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color:
                      data?.percentage > 0
                        ? "#00B894"
                        : data?.percentage < 0
                          ? "#FF2E57"
                          : "#9E9E9E", // Grey color for growth = 0
                    fontWeight: "bold",
                  }}
                >
                  {data?.percentage > 0 ? (
                    <TrendingUpOutlined fontSize="8px" />
                  ) : data?.percentage < 0 ? (
                    <TrendingDownOutlined fontSize="8px" />
                  ) : (
                    <TrendingUpOutlined fontSize="8px" /> // You can use any icon here for growth = 0 or leave it empty
                  )}
                  {data?.percentage !== 0 && (data?.percentage > 0 ? "+" : "")}
                  {Math.round(data?.percentage)}%
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>
    );
  };

  return (
    <Box sx={{ display: "flex" }}>
      <Box component="main" sx={{ flexGrow: 1 }}>
        {isOnboardingRequired ? (
          <OnboardingProgress />
        ) : (
          <>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}
            >
              <CommonHeader
                title="Dashboard"
                subTitle={
                  <>
                    {getLast30DaysRange()}{" "}
                    <AutorenewRounded
                      onClick={refreshData}
                      sx={{ cursor: "pointer" }}
                    />{" "}
                  </>
                }
              />
              <Box sx={{ display: "flex" }}>
                <Select
                  value={selectedDays}
                  onChange={handleTimePeriodChange} // Add this handler
                  displayEmpty
                  inputProps={{ "aria-label": "Select time period" }}
                  sx={{
                    textTransform: "capitalize",
                    color: "black",
                    minWidth: 120,
                    height: 40,
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "green",
                      },
                      "&:focus fieldset": {
                        borderColor: "green",
                      },
                    },
                  }}
                >
                  <MenuItem value={-24}>Last 24 Hours</MenuItem>
                  <Divider />
                  <MenuItem value={7}>Last 7 Days</MenuItem>
                  <Divider />
                  <MenuItem value={30}>Last 30 Days</MenuItem>
                  <Divider />
                  <MenuItem value={60}>Last 60 Days</MenuItem>
                  <Divider />
                  <MenuItem value={90}>Last 90 Days</MenuItem>
                </Select>
              </Box>
            </Box>

            {(loadingWebsiteId || loadingWebsiteDetails || loadingConsent) ? (
              <Loader />
            ) : (
              <>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={4}>
                    <StatCard
                      icon={<RemoveRedEyeOutlined sx={{ color: "#00B894" }} />}
                      title="Page Views"
                      value={pageVisitStats?.pageviews?.current}
                      growth={pageVisitStats?.pageviews?.percentage_change}
                      isLoading={loadingStates.pageVisitStats}
                      gradientColors={['#00B894', '#00E7AC']}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <StatCard
                      icon={<PersonOutlineOutlined sx={{ color: "#3CC3DF" }} />}
                      title="Unique Visitors"
                      value={pageVisitStats?.unique_visitors?.current}
                      growth={pageVisitStats?.unique_visitors?.percentage_change}
                      isLoading={loadingStates.pageVisitStats}
                      gradientColors={['#3CC3DF', '#6DD5ED']}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <StatCard
                      icon={<CheckCircleOutline sx={{ color: "#6366F1" }} />}
                      title="Consents"
                      value={consentStatsData?.consents?.current}
                      growth={consentStatsData?.consents?.percentage_change}
                      isLoading={loadingStates.consentStats}
                      gradientColors={['#6366F1', '#8B5CF6']}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={4}>
                    <StatCard
                      icon={<TotalDataRequests />}
                      title="Total Data Requests"
                      value={requestStats?.total_requests?.current}
                      growth={requestStats?.total_requests?.percentage_change}
                      isLoading={loadingStates.requestStats}
                      gradientColors={['#F59E0B', '#FBBF24']}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <StatCard
                      icon={<OpenDataRequests />}
                      title="Open Data Requests"
                      value={requestStats?.open_requests?.current}
                      growth={requestStats?.open_requests?.percentage_change}
                      isLoading={loadingStates.requestStats}
                      gradientColors={['#10B981', '#34D399']}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <StatCard
                      icon={<ClosedDataRequests />}
                      title="Closed Data Requests"
                      value={requestStats?.closed_requests?.current}
                      growth={requestStats?.closed_requests?.percentage_change}
                      isLoading={loadingStates.requestStats}
                      gradientColors={['#EF4444', '#F87171']}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 3,
                        height: "100%",
                        borderRadius: 2,
                        border: "1px solid #E5E7EB",
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      {/* Loading Progress Bar for Visitors Panel */}
                      {loadingStates.visitors && (
                        <LinearProgress
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 3,
                            background: 'linear-gradient(90deg, rgba(0, 184, 148, 0.1) 0%, rgba(60, 195, 223, 0.1) 100%)',
                            '& .MuiLinearProgress-bar': {
                              background: 'linear-gradient(90deg, #00B894 0%, #3CC3DF 100%)',
                            },
                          }}
                        />
                      )}
                      
                      <Box sx={{ 
                        opacity: loadingStates.visitors ? 0.6 : 1,
                        transition: 'opacity 0.3s ease-in-out'
                      }}>
                        <Typography
                          variant="h6"
                          gutterBottom
                          fontWeight="bold"
                          sx={{ 
                            color: "#111827", 
                            mb: 2,
                            background: 'linear-gradient(90deg, #00B894 0%, #3CC3DF 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text'
                          }}
                        >
                          Visitors
                        </Typography>
                        
                        {loadingStates.visitors ? (
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: 2,
                            height: 250,
                            p: 2
                          }}>
                            <Skeleton 
                              variant="text" 
                              width="40%" 
                              height={24}
                              animation="wave"
                              sx={{ bgcolor: '#00B89420' }}
                            />
                            <Skeleton 
                              variant="rectangular" 
                              width="100%" 
                              height={180}
                              animation="wave"
                              sx={{ 
                                bgcolor: '#00B89415',
                                borderRadius: 2
                              }}
                            />
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <Skeleton 
                                variant="rectangular" 
                                width={100} 
                                height={20}
                                animation="wave"
                                sx={{ bgcolor: '#00B89420', borderRadius: 1 }}
                              />
                              <Skeleton 
                                variant="rectangular" 
                                width={120} 
                                height={20}
                                animation="wave"
                                sx={{ bgcolor: '#3CC3DF20', borderRadius: 1 }}
                              />
                            </Box>
                          </Box>
                        ) : (
                          <LineChart
                            width={700}
                            height={250}
                            data={visitors}
                            margin={{
                              top: 8,
                              right: 30,
                              left: 20,
                              bottom: 5,
                              color: "#525455",
                            }}
                          >
                            <XAxis
                              dataKey="date"
                              stroke="#999"
                              tick={{
                                fill: "#64748B",
                                fontSize: 12,
                                fontWeight: 400,
                              }}
                            />
                            <YAxis
                              stroke="#999"
                              tick={{
                                fill: "#64748B",
                                fontSize: 12,
                                fontWeight: 400,
                              }}
                            />
                            <Tooltip />
                            <Line
                              type="monotone"
                              dataKey="pageViews"
                              stroke="#00B894"
                              strokeWidth={2}
                              dot={true}
                            />
                            <Line
                              type="monotone"
                              dataKey="uniqueVisitors"
                              stroke="#3CC3DF"
                              strokeWidth={2}
                              dot={false}
                              strokeDasharray="5 5"
                            />
                            <Legend
                              layout="horizontal"
                              verticalAlign="top"
                              align="right"
                            />
                          </LineChart>
                        )}
                      </Box>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 3,
                        height: "100%",
                        borderRadius: 2,
                        border: "1px solid #E5E7EB",
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                        overflowY: "auto",
                        maxHeight: "350px",
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      {/* Loading Progress Bar for Countries Panel */}
                      {loadingStates.countries && (
                        <LinearProgress
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 3,
                            background: 'linear-gradient(90deg, rgba(0, 184, 148, 0.1) 0%, rgba(0, 231, 172, 0.1) 100%)',
                            '& .MuiLinearProgress-bar': {
                              background: 'linear-gradient(90deg, #00B894 0%, #00E7AC 100%)',
                            },
                          }}
                        />
                      )}
                      
                      <Typography
                        variant="h6"
                        gutterBottom
                        fontWeight="bold"
                        sx={{ 
                          color: "#111827", 
                          mb: 2,
                          background: 'linear-gradient(90deg, #00B894 0%, #00E7AC 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text'
                        }}
                      >
                        Consent By Countries
                      </Typography>
                      
                      <Box sx={{ 
                        opacity: loadingStates.countries ? 0.6 : 1,
                        transition: 'opacity 0.3s ease-in-out',
                        overflowY: loadingStates.countries ? 'hidden' : 'auto',
                        maxHeight: "280px"
                      }}>
                        {loadingStates.countries ? (
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: 2,
                            p: 1
                          }}>
                            {[1, 2, 3, 4, 5].map((index) => (
                              <Box key={index} sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 2,
                                p: 1
                              }}>
                                <Skeleton 
                                  variant="circular" 
                                  width={35} 
                                  height={35}
                                  animation="wave"
                                  sx={{ bgcolor: '#00B89420' }}
                                />
                                <Box sx={{ flex: 1 }}>
                                  <Skeleton 
                                    variant="text" 
                                    width="60%" 
                                    height={16}
                                    animation="wave"
                                    sx={{ bgcolor: '#00B89420', mb: 0.5 }}
                                  />
                                  <Skeleton 
                                    variant="rectangular" 
                                    width="100%" 
                                    height={4}
                                    animation="wave"
                                    sx={{ bgcolor: '#00E7AC15', borderRadius: 1 }}
                                  />
                                </Box>
                                <Skeleton 
                                  variant="rectangular" 
                                  width={50} 
                                  height={20}
                                  animation="wave"
                                  sx={{ bgcolor: '#00B89420', borderRadius: 1 }}
                                />
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          (Array.isArray(countries)
                            ? [...countries].sort((a, b) => b.count - a.count)
                            : []
                          ).map((country) => {
                        const countryName = country?.name ?? "Other"; // Set default name
                        const isoCode = country?.iso_code;
                        const percentage =
                          typeof country.percentage === "string"
                            ? parseFloat(country.percentage.replace("%", ""))
                            : country.percentage;
                        return (
                          <Box
                            key={countryName}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: 2,
                              gap: 2,
                            }}
                          >
                            {/* Left Section */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flex: 1,
                                gap: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 35,
                                  height: 35,
                                  borderRadius: "50%",
                                  border: "1px solid #DADADA",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              >
                                <Flag
                                  code={isoCode}
                                  fallback={
                                    <img
                                      src={"/images/default.svg"}
                                      alt="default"
                                      style={{
                                        maxWidth: "100%",
                                        maxHeight: "100%",
                                        objectFit: "contain",
                                      }}
                                    />
                                  }
                                  alt={`${isoCode} flag`}
                                  style={{ width: 16, height: 16 }}
                                />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography
                                  variant="body2"
                                  color="#525455"
                                  sx={{ fontWeight: 400, marginBottom: 0.5 }}
                                >
                                  {countryName}
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={percentage}
                                  sx={{
                                    height: 4,
                                    borderRadius: 3,
                                    backgroundColor: "#F0F0F0",
                                    "& .MuiLinearProgress-bar": {
                                      backgroundColor: "#00B894",
                                    },
                                  }}
                                />
                              </Box>
                            </Box>

                            {/* Right Section */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  background:
                                    country?.percentage > 0
                                      ? "#F0FFFC"
                                      : country?.percentage < 0
                                        ? "#FFEFF2"
                                        : "#E0E0E0",
                                  padding: "4px 8px",
                                  borderRadius: 2,
                                  mr: 2,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color:
                                      country?.percentage > 0
                                        ? "#00B894"
                                        : country?.percentage < 0
                                          ? "#FF2E57"
                                          : "#9E9E9E",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {country?.percentage > 0 ? (
                                    <TrendingUpOutlined fontSize="8px" />
                                  ) : country?.percentage < 0 ? (
                                    <TrendingDownOutlined fontSize="8px" />
                                  ) : (
                                    <TrendingUpOutlined fontSize="8px" />
                                  )}
                                  {country?.percentage !== 0 &&
                                    (country?.percentage > 0 ? "+" : "")}
                                  {Math.round(country?.percentage)}%
                                </Typography>
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  color: "#525455",
                                }}
                              >
                                {formatNumber(country?.count, timezone)}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })
                        )}
                      </Box>
                    </Card>
                  </Grid>

                  {/* Consents Chart */}
                  <Grid item xs={12} md={8}>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 3,
                        height: "100%",
                        borderRadius: 2,
                        border: "1px solid #E5E7EB",
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      {/* Loading Progress Bar for Consents Panel */}
                      {loadingStates.consents && (
                        <LinearProgress
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 3,
                            background: 'linear-gradient(90deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
                            '& .MuiLinearProgress-bar': {
                              background: 'linear-gradient(90deg, #6366F1 0%, #8B5CF6 100%)',
                            },
                          }}
                        />
                      )}
                      
                      <Box sx={{ 
                        opacity: loadingStates.consents ? 0.6 : 1,
                        transition: 'opacity 0.3s ease-in-out'
                      }}>
                        <Typography
                          variant="h6"
                          gutterBottom
                          fontWeight="bold"
                          sx={{ 
                            color: "#111827", 
                            mb: 2,
                            background: 'linear-gradient(90deg, #6366F1 0%, #8B5CF6 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text'
                          }}
                        >
                          Consents
                        </Typography>
                        
                        {loadingStates.consents ? (
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: 2,
                            height: 300,
                            p: 2
                          }}>
                            <Skeleton 
                              variant="text" 
                              width="50%" 
                              height={24}
                              animation="wave"
                              sx={{ bgcolor: '#6366F120' }}
                            />
                            <Skeleton 
                              variant="rectangular" 
                              width="100%" 
                              height={220}
                              animation="wave"
                              sx={{ 
                                bgcolor: '#6366F115',
                                borderRadius: 2
                              }}
                            />
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                              <Skeleton 
                                variant="rectangular" 
                                width={80} 
                                height={16}
                                animation="wave"
                                sx={{ bgcolor: '#6366F120', borderRadius: 1 }}
                              />
                              <Skeleton 
                                variant="rectangular" 
                                width={90} 
                                height={16}
                                animation="wave"
                                sx={{ bgcolor: '#F59E0B20', borderRadius: 1 }}
                              />
                              <Skeleton 
                                variant="rectangular" 
                                width={85} 
                                height={16}
                                animation="wave"
                                sx={{ bgcolor: '#10B98120', borderRadius: 1 }}
                              />
                              <Skeleton 
                                variant="rectangular" 
                                width={75} 
                                height={16}
                                animation="wave"
                                sx={{ bgcolor: '#EF444420', borderRadius: 1 }}
                              />
                            </Box>
                          </Box>
                        ) : (
                          <LineChart
                            width={700}
                            height={300}
                            data={consents}
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          >
                            <XAxis
                              dataKey="date"
                              stroke="#999"
                              tick={{
                                fill: "#64748B",
                                fontSize: 12,
                                fontWeight: 400,
                              }}
                            />
                            <YAxis
                              stroke="#999"
                              tick={{
                                fill: "#64748B",
                                fontSize: 12,
                                fontWeight: 400,
                              }}
                            />
                            <Tooltip />
                            <Line
                              type="monotone"
                              dataKey="fullConsents"
                              stroke="#6366f1"
                              strokeWidth={2}
                              dot={false}
                            />
                            <Line
                              type="monotone"
                              dataKey="customConsents"
                              stroke="#f59e0b"
                              strokeWidth={2}
                              dot={false}
                            />
                            <Line
                              type="monotone"
                              dataKey="onlyNecessary"
                              stroke="#10b981"
                              strokeWidth={2}
                              dot={false}
                            />
                            <Line
                              type="monotone"
                              dataKey="noDecision"
                              stroke="#ef4444"
                              strokeWidth={2}
                              dot={false}
                            />
                            <Legend
                              layout="horizontal"
                              verticalAlign="top"
                              align="right"
                            />
                          </LineChart>
                        )}
                      </Box>
                    </Card>
                  </Grid>

                  {/* Devices Chart */}
                  <Grid item xs={12} md={4}>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 3,
                        height: "100%",
                        borderRadius: 2,
                        border: "1px solid #E5E7EB",
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      {/* Loading Progress Bar for Devices Panel */}
                      {loadingStates.devices && (
                        <LinearProgress
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 3,
                            background: 'linear-gradient(90deg, rgba(16, 185, 129, 0.1) 0%, rgba(52, 211, 153, 0.1) 100%)',
                            '& .MuiLinearProgress-bar': {
                              background: 'linear-gradient(90deg, #10B981 0%, #34D399 100%)',
                            },
                          }}
                        />
                      )}
                      
                      <Box sx={{ 
                        opacity: loadingStates.devices ? 0.6 : 1,
                        transition: 'opacity 0.3s ease-in-out'
                      }}>
                        <Typography
                          variant="h6"
                          gutterBottom
                          fontWeight="bold"
                          sx={{ 
                            color: "#111827", 
                            mb: 2,
                            background: 'linear-gradient(90deg, #10B981 0%, #34D399 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text'
                          }}
                        >
                          Consent By Devices
                        </Typography>
                        
                        {loadingStates.devices ? (
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            height: 300,
                            p: 2
                          }}>
                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                              <Skeleton 
                                variant="rectangular" 
                                width={60} 
                                height={16}
                                animation="wave"
                                sx={{ bgcolor: '#10B98120', borderRadius: 1 }}
                              />
                              <Skeleton 
                                variant="rectangular" 
                                width={70} 
                                height={16}
                                animation="wave"
                                sx={{ bgcolor: '#6366F120', borderRadius: 1 }}
                              />
                              <Skeleton 
                                variant="rectangular" 
                                width={65} 
                                height={16}
                                animation="wave"
                                sx={{ bgcolor: '#1E90FF20', borderRadius: 1 }}
                              />
                            </Box>
                            <Skeleton 
                              variant="circular" 
                              width={200} 
                              height={200}
                              animation="wave"
                              sx={{ 
                                bgcolor: '#10B98115'
                              }}
                            />
                          </Box>
                        ) : (
                          <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <PieChart width={400} height={300}>
                          {/* Main Pie Chart */}
                          <Pie
                            data={device}
                            cx="50%"
                            cy="50%"
                            innerRadius={80}
                            outerRadius={120}
                            fill="#8884d8"
                            dataKey="value"
                            nameKey="name"
                            label={({ name, value }) =>
                              `${formatNumber(value, timezone)}`
                            }
                          >
                            {(Array.isArray(device) ? device : []).map(
                              (_, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={
                                    [
                                      "#10b981",
                                      "#6366f1",
                                      "#1E90FF",
                                      "#FFA502",
                                      "#D7D7D7",
                                    ][index]
                                  }
                                />
                              )
                            )}
                          </Pie>
                          <Tooltip
                            content={<CustomTooltip />}
                            wrapperStyle={{ outline: "none" }}
                          />
                          <Legend
                            verticalAlign="top"
                            align="left"
                            iconType="circle"
                            formatter={(value) => (
                              <span
                                style={{ color: "#525455", fontSize: "14px" }}
                              >
                                {value}
                              </span>
                            )}
                            wrapperStyle={{
                              display: "flex",
                              flexWrap: "wrap",
                              paddingLeft: "10px",
                              justifyContent: "flex-start",
                              gap: "10px",
                            }}
                          />
                        </PieChart>
                          </Box>
                        )}
                      </Box>
                    </Card>
                  </Grid>
                </Grid>
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default Dashboard;
